// Parsing, formatting and matching, driven by the registry of criteria
// (`criteria/index.ts`): nothing here names a criterion. A field taking
// several values (`many`) is carried as `a,b` in the URL and passes a game
// matching any of them.
import { FILTER_CRITERIA } from './criteria/index.ts';
import type { FilterCriterion, FilterKey } from './criterion.ts';
import { valuesOf, type FilterableGame, type GameFilter } from '@chess/api-model';

type Item = string;
type Criterion = FilterCriterion<FilterKey>;
const fmt = (c: Criterion, v: Item): string | undefined => c.format(v as never);

/** The values a filter sets for a criterion that say something (for text, not blank); [] for none. */
export function filterValues(filter: GameFilter, c: Criterion): Item[] {
  return valuesOf(filter[c.key] as Item | readonly Item[] | undefined).filter((v) => fmt(c, v) !== undefined);
}

/** The filter with a criterion set to these values: none clears it, one is kept as one value. */
export function withFilterValues(filter: GameFilter, c: Criterion, values: readonly Item[]): GameFilter {
  const unique = [...new Set(values)];
  const next: Record<string, unknown> = { ...filter };
  if (unique.length === 0) delete next[c.key];
  else next[c.key] = unique.length === 1 || !c.many ? unique[0] : unique;
  return next as GameFilter;
}

/** The URL query parameter for each field; other plugins link with these (the home dashboard). */
export const FILTER_QUERY = FILTER_CRITERIA.reduce((all, c) => ({ ...all, [c.key]: c.query }), {} as Record<FilterKey, string>);

/** Whether a game passes the filter. The opening and ending need the game's PGN (its headers suffice). */
export function matchesFilter(game: FilterableGame, filter: GameFilter, now: number = Date.now()): boolean {
  for (const c of FILTER_CRITERIA) {
    const values = filterValues(filter, c);
    if (values.length && !values.some((v) => c.matches(game, v as never, filter, now))) return false;
  }
  if (filter.ids && (game.id === undefined || !filter.ids.includes(game.id))) return false;
  return true;
}

/** Whether the filter includes every game. */
export function isEmptyFilter(filter: GameFilter): boolean {
  return !filter.ids && FILTER_CRITERIA.every((c) => filterValues(filter, c).length === 0);
}

/** The filter from a URL query string ("?result=loss,draw&colour=black"); unknown values are ignored. */
export function parseFilter(search: string): GameFilter {
  const params = new URLSearchParams(search);
  let filter: GameFilter = {};
  for (const c of FILTER_CRITERIA) {
    const raw = params.get(c.query);
    if (raw === null || !raw.trim()) continue;
    const parts = c.many ? raw.split(',') : [raw];
    const values = parts.map((p) => p.trim()).filter(Boolean).map((p) => c.parse(p) as Item | undefined).filter((v): v is Item => v !== undefined);
    filter = withFilterValues(filter, c, values);
  }
  return filter;
}

/** The URL query string for a filter, "" for an empty one, else "?…" in the registry's order. */
export function formatFilter(filter: GameFilter): string {
  const params = new URLSearchParams();
  for (const c of FILTER_CRITERIA) {
    const texts = filterValues(filter, c).map((v) => fmt(c, v)!);
    if (texts.length) params.set(c.query, texts.join(','));
  }
  const text = params.toString().replace(/%2C/g, ',');
  return text ? `?${text}` : '';
}
